import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import type { LinkPricesResponse, OptionPricesDTO, OptionPricesResponse } from "../productsApi";
import { getOptionPrices, linkOptionPrices, unlinkOptionPrices, updateOptionPrices } from "../productsApi";
import { isProductSellingPricesQuery } from "../useProductSellingPrices";

export const useOptionPrices = (productId: string, optionId: string) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["products", productId, "options", optionId, "prices"],
    queryFn: ({ signal }) => getOptionPrices(productId, optionId, signal),
  });

  const updatePricesMutation = useMutation<OptionPricesResponse, unknown, OptionPricesDTO>({
    mutationFn: (r) => updateOptionPrices(productId, optionId, r),
    onSuccess: async () => {
      enqueueSnackbar("Cennik został zaktualizowany", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
      await queryClient.invalidateQueries({ queryKey: ["products", productId, "options", optionId, "prices"] });
      await queryClient.invalidateQueries({ queryKey: ["pricesOnDate"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zaktualizować cennika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const linkOptionPricesMutation = useMutation<
    LinkPricesResponse,
    unknown,
    { productId: string; optionId: string; usePricesFrom: { productId: string; optionId: string } }
  >({
    mutationFn: ({ productId, optionId, usePricesFrom }) => linkOptionPrices(productId, optionId, usePricesFrom),
    onSuccess: async () => {
      enqueueSnackbar("Cennik został przypisany", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
      await queryClient.invalidateQueries({ queryKey: ["products", productId, "options", optionId, "prices"] });
      await queryClient.invalidateQueries({ queryKey: ["pricesOnDate"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się przypisać cennika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  const unlinkOptionPricesMutation = useMutation<
    void,
    unknown,
    { productId: string; optionId: string; copyPrices: boolean }
  >({
    mutationFn: ({ productId, optionId, copyPrices }) => unlinkOptionPrices(productId, optionId, copyPrices),
    onSuccess: async () => {
      enqueueSnackbar("Przypisanie cennika zostało usunięte", { variant: "success" });
      await queryClient.invalidateQueries({ predicate: isProductSellingPricesQuery(productId) });
      await queryClient.invalidateQueries({ queryKey: ["products", productId, "options", optionId, "prices"] });
      await queryClient.invalidateQueries({ queryKey: ["pricesOnDate"] });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się usunąć przypisania cennika. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    data,
    isLoading,
    updatePrices: (request: OptionPricesDTO) => updatePricesMutation.mutateAsync(request),
    linkOptionPrices: (productId: string, optionId: string, usePricesFrom: { productId: string; optionId: string }) =>
      linkOptionPricesMutation.mutateAsync({ productId, optionId, usePricesFrom }),
    unlinkOptionPrices: (productId: string, optionId: string, copyPrices: boolean) =>
      unlinkOptionPricesMutation.mutateAsync({ productId, optionId, copyPrices }),
  };
};
